import { useMemo, useState } from "react";
import Footer from "../../components/layouts/Footer";
import SellerCard from "../../components/SellerCard";
import { Seller } from "../../types";
import { Link } from "react-router-dom";
import { usePersistStore } from "../../store";
import { Skeleton } from "boneyard-js/react";
import { useSellers } from "../../hooks/queries";

function Sellers() {
  const { currentLocation } = usePersistStore((s: any) => s);
  const [keyword, setKeyword] = useState<string>("");

  const { data = [], isLoading } = useSellers({
    keyword,
    location: currentLocation,
  });
  const isReady = !isLoading;

  const list: Seller[] = useMemo(
    () =>
      (data || []).map((seller: any) => ({
        _id: seller?._id,
        name: seller?.name,
        imageUrl: seller?.fileUrl,
        sold: seller?.salesCount ?? 0,
        distance: seller?.distance,
        profileUrl: `/seller/${seller?._id}`,
        city: seller?.city,
        state: seller?.state,
        speciality: seller?.speciality,
        badges: seller?.badges || [],
      })),
    [data]
  );

  return (
    <>
      <div className="container px-md-0 px-3">
        <div className="sellersPage">
          <div className="breadCrumb mt-4 mb-3">
            <div className="d-flex gap-3">
              <Link to="/">
                <p className="m-0">Home</p>
              </Link>
              <span>/</span>
              <h4 className="mb-0">Our artists</h4>
            </div>
          </div>

          <header className="sellersPage__hero">
            <span className="sellersPage__heroKicker">Featured artists</span>
            <h1 className="sellersPage__heroTitle">Discover our artists</h1>
            <p className="sellersPage__heroSubtitle">
              Browse the makers behind every piece on Boll-e&trade; — sculptors,
              painters, and craftspeople bringing original work to the
              marketplace.
            </p>
            <div className="sellersPage__searchBar">
              <i className="bi bi-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search by artist name..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
              {keyword && (
                <button
                  type="button"
                  className="sellersPage__searchClear"
                  onClick={() => setKeyword("")}
                  aria-label="Clear search"
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              )}
            </div>
          </header>

          <section className="row justify-content-start">
            {!isReady &&
              Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={`sk-${i}`}
                  className="custom-col sellerCardCol mb-sm-4"
                >
                  <Skeleton name="seller-card" loading={true}>
                    <span style={{ display: "block", minHeight: 220 }} />
                  </Skeleton>
                </div>
              ))}
            {isReady && list.length === 0 && (
              <div className="col-12">
                <div className="sellersPage__empty">
                  <i
                    className="bi bi-people sellersPage__emptyIcon"
                    aria-hidden="true"
                  />
                  <div className="sellersPage__emptyTitle">
                    No artists found
                  </div>
                  <div className="sellersPage__emptyText">
                    {keyword
                      ? "Try a different search term."
                      : "Check back soon — new artists join all the time."}
                  </div>
                </div>
              </div>
            )}
            {isReady &&
              list.map((seller: Seller, index) => (
                <SellerCard
                  seller={seller}
                  key={index}
                  showFollowButton={true}
                />
              ))}
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default Sellers;
